// Reliability status of a claim, the scale investigative reports print next to each fact: Official document,
// Corroborated (at least two independent outlets), Single source, Hypothesis. Computed from the claim's evidence and
// the source registry (`source-registry.ts`): sources of one outlet, owner or independence group count once, and what
// an actor says about itself never corroborates a claim on its own. Not stored: it changes as evidence and the
// registry change.

import type { ClaimKind } from './claims';
import type { ReportLanguage } from './report-language';
import type { SourceClass } from './source-registry';

export const RELIABILITY_STATUSES = ['official_document', 'corroborated', 'single_source', 'hypothesis'] as const;
export type ReliabilityStatus = (typeof RELIABILITY_STATUSES)[number];
export const isReliabilityStatus = (v: unknown): v is ReliabilityStatus => RELIABILITY_STATUSES.includes(v as ReliabilityStatus);

export const RELIABILITY_LABELS: Record<ReportLanguage, Record<ReliabilityStatus, string>> = {
  en: {
    official_document: 'Official document',
    corroborated: 'Corroborated',
    single_source: 'Single source',
    hypothesis: 'Hypothesis',
  },
  fr: {
    official_document: 'Document officiel',
    corroborated: 'Corroboré',
    single_source: 'Source unique',
    hypothesis: 'Hypothèse',
  },
};

export const RELIABILITY_MEANINGS: Record<ReportLanguage, Record<ReliabilityStatus, string>> = {
  en: {
    official_document: 'Backed by an official document: a register, ruling, filing or official record.',
    corroborated: 'Reported by at least two independent sources (different outlets, owners and groups).',
    single_source: 'Rests on one source, or on sources that count as one (same outlet, owner or group, or the actor itself).',
    hypothesis: 'No source backs it yet, or it is an analyst’s assessment.',
  },
  fr: {
    official_document: 'Appuyé par un document officiel : registre, décision, dépôt ou acte officiel.',
    corroborated: 'Rapporté par au moins deux sources indépendantes (médias, propriétaires et groupes différents).',
    single_source:
      'Repose sur une seule source, ou sur des sources qui comptent pour une (même média, propriétaire ou groupe, ou l’acteur lui-même).',
    hypothesis: 'Aucune source ne l’appuie encore, ou il s’agit d’une appréciation de l’analyste.',
  },
};

/** One source behind a claim, as the registry sees it. */
export interface ReliabilitySource {
  groupKey: string;
  sourceClass: SourceClass | null;
}

export interface ClaimReliability {
  status: ReliabilityStatus;
  /** Independent sources that can corroborate it: distinct groups, the actor's own sources left out. */
  independentSources: number;
  /** The groups behind it, in words (the outlet's group, owner or name; the publisher; the site). */
  groups: string[];
}

/** Strongest first: a list of claims shows the best support any of them has. */
const RANK: Record<ReliabilityStatus, number> = { official_document: 3, corroborated: 2, single_source: 1, hypothesis: 0 };

/**
 * The reliability status of a claim. An assessment (an analyst's judgement), or a claim with no document or cited
 * source behind it, is a Hypothesis. Otherwise an official document among its sources makes it Official document;
 * two or more independent groups (the actor's own sources left out) make it Corroborated; anything else is a Single
 * source.
 */
export function reliabilityOf(c: { claimKind: ClaimKind }, sources: readonly (ReliabilitySource & { group?: string })[]): ClaimReliability {
  const independent = new Map<string, string>();
  for (const s of sources)
    if (s.sourceClass !== 'actor_itself' && !independent.has(s.groupKey)) independent.set(s.groupKey, s.group ?? s.groupKey);
  const groups = [...new Map(sources.map(s => [s.groupKey, s.group ?? s.groupKey])).values()];
  const base = { independentSources: independent.size, groups };
  if (c.claimKind === 'assessment' || !sources.length) return { status: 'hypothesis', ...base };
  if (sources.some(s => s.sourceClass === 'official_document')) return { status: 'official_document', ...base };
  if (independent.size >= 2) return { status: 'corroborated', ...base };
  return { status: 'single_source', ...base };
}

/** The strongest status among several claims (an item reached by several claims); null for none. */
export function strongestReliability(list: readonly (ReliabilityStatus | null | undefined)[]): ReliabilityStatus | null {
  let best: ReliabilityStatus | null = null;
  for (const s of list) if (s && (!best || RANK[s] > RANK[best])) best = s;
  return best;
}
